import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import * as smartbillSvc from '../../services/smartbill';
import { FilterClearBtn, FilterCountHint, FilterDateRange, FilterPill } from '../../components/Shared/FilterPill';
import InvoiceEditModal from '../../components/Shared/InvoiceEditModal';
import FacturaModal from '../../components/Shared/FacturaModal';
import { lineAmountsRon, resolveLineTip, OBJ_SVC_LABEL } from '../../utils/invoiceMath';
import * as borderouSvc from '../../services/borderou';
import './FacturarePage.css';
import '../Clienti/ClientiPage.css';

const OBJ_STATUS_GROUPS = {
  incasate:   ['platita'],
  neincasate: ['neplatita'],
  partial:    ['incasata_partial', 'facturata_partial'],
  anulate:    ['anulata', 'stornata'],
};
const OBJ_STATUS_LABELS = [
  { v: '',           l: 'Status' },
  { v: 'incasate',   l: 'Incasate' },
  { v: 'neincasate', l: 'Neincasate' },
  { v: 'partial',    l: 'Incasat partial' },
  { v: 'anulate',    l: 'Anulate / Stornate' },
];

/* ── helpers ── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtMoney(v, moneda) {
  if (v === null || v === undefined) return '—';
  return `${parseFloat(v).toFixed(2)} ${moneda || 'RON'}`;
}

const STATUS_COLORS = {
  neplatita:         { bg: '#fef3c7', color: '#92400e' },
  platita:           { bg: '#d1fae5', color: '#065f46' },
  anulata:           { bg: '#f3f4f6', color: '#6b7280' },
  stornata:          { bg: '#fee2e2', color: '#991b1b' },
  facturata:         { bg: '#dbeafe', color: '#1d4ed8' },
  facturata_partial: { bg: '#ede9fe', color: '#6d28d9' },
  depasita:          { bg: '#fce7f3', color: '#9d174d' },
  incasata_partial:  { bg: '#ede9fe', color: '#6d28d9' },
};

const STATUS_LABELS = {
  neplatita:         'Neplatita',
  platita:           'Platita',
  anulata:           'Anulata',
  stornata:          'Stornata',
  facturata:         'Facturata',
  facturata_partial: 'Fact. partial',
  depasita:          'Depasita',
  incasata_partial:  'Incasat partial',
};

function getStatusLabel(status, tip) {
  if (tip === 'fiscala' && status === 'platita') return 'Incasata';
  return STATUS_LABELS[status] || status;
}

/* ── FacturarePage ── */
export default function FacturarePage() {
  const { viewMode } = useAuth();
  const location = useLocation();
  const [documente, setDocumente] = useState([]);
  const [loading, setLoading]     = useState(true);
  const { toasts, show: showToast } = useToast();

  /* server-side filters */
  const [fTip,      setFTip]      = useState('');
  const [fStatus,   setFStatus]   = useState('');
  const [fCompanie, setFCompanie] = useState('');
  const [fDataFrom, setFDataFrom] = useState('');
  const [fDataTo,   setFDataTo]   = useState('');

  /* client-side filters */
  const [fClient,      setFClient]      = useState('');
  const [fProcedura,   setFProcedura]   = useState('');
  const [fTipServiciu, setFTipServiciu] = useState('');

  const [downloading,   setDownloading]   = useState(null);
  const [editDoc,       setEditDoc]       = useState(null);
  const [reEditDoc,     setReEditDoc]     = useState(null);
  const [convertDocFct, setConvertDocFct] = useState(null);

  /* ── tabs: "facturi" (invoice view) vs "obiecte" (line-item view) ── */
  const [activeTab, setActiveTab] = useState('facturi');

  /* ── sold rezerva borderou (global) ── */
  const [showReserve, setShowReserve]     = useState(false);
  const [reserveInfo, setReserveInfo]     = useState(null);   // { sold, updated_at, updated_by }
  const [reserveInput, setReserveInput]   = useState('');
  const [reserveBusy, setReserveBusy]     = useState(false);
  const [reserveSold, setReserveSold]     = useState(borderouSvc.getCachedSold());

  useEffect(() => {
    borderouSvc.getReserve().catch(() => {});
    return borderouSvc.subscribeReserve(setReserveSold);
  }, []);

  const reserveLow = reserveSold !== null && reserveSold <= borderouSvc.RESERVE_LOW_THRESHOLD;

  const openReserve = async () => {
    setShowReserve(true);
    setReserveInfo(null);
    try {
      const data = await borderouSvc.getReserve();
      setReserveInfo(data);
      setReserveInput(data.sold ?? '0');
    } catch {
      showToast('Eroare la incarcarea soldului');
    }
  };

  const saveReserve = async () => {
    const val = parseFloat(String(reserveInput).replace(',', '.'));
    if (Number.isNaN(val)) { showToast('Introdu un numar valid'); return; }
    setReserveBusy(true);
    try {
      const data = await borderouSvc.updateReserve(val);
      setReserveInfo(data);
      showToast('✓ Sold rezerva actualizat');
      setShowReserve(false);
    } catch {
      showToast('Eroare la salvare');
    } finally {
      setReserveBusy(false);
    }
  };

  /* Obiecte tab — its own dataset (always fiscala) + its own filters */
  const [oDocs,        setODocs]        = useState([]);
  const [oLoading,     setOLoading]     = useState(false);
  const [oCompanie,    setOCompanie]    = useState('');
  const [oDataFrom,    setODataFrom]    = useState('');
  const [oDataTo,      setODataTo]      = useState('');
  const [oClient,      setOClient]      = useState('');
  const [oTipServiciu, setOTipServiciu] = useState('');
  const [oStatus,      setOStatus]      = useState('');

  const patchDocEverywhere = (updated) => {
    setDocumente(ds => ds.map(d => d.id === updated.id ? updated : d));
    setODocs(ds => ds.map(d => d.id === updated.id ? updated : d));
  };

  const handleStatusChange = async (docId, newStatus) => {
    try {
      const res = await smartbillSvc.patchDocument(docId, { status_plata: newStatus });
      patchDocEverywhere(res.data.document);
    } catch {
      showToast('Eroare la actualizare status');
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (fTip)      params.tip       = fTip;
    if (fStatus)   params.status    = fStatus;
    if (fCompanie) params.companie  = fCompanie;
    if (fDataFrom) params.data_from = fDataFrom;
    if (fDataTo)   params.data_to   = fDataTo;
    smartbillSvc.getAllDocuments(params)
      .then(r => setDocumente(r.data.documente || []))
      .catch(() => showToast('Eroare la incarcarea documentelor'))
      .finally(() => setLoading(false));
  }, [fTip, fStatus, fCompanie, fDataFrom, fDataTo, viewMode]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // External links (e.g. the daily report email) open a specific invoice
    // via a query param, same deep-link pattern as ClientiPage/OficiuPage.
    const id = new URLSearchParams(location.search).get('openInvoiceId');
    if (!id || documente.length === 0) return;
    const doc = documente.find(d => d.id === Number(id));
    if (!doc) return;
    setEditDoc(doc);
    window.history.replaceState({}, '', location.pathname);
  }, [documente, location.search, location.pathname]);

  /* unique options for client-side dropdowns — auto-update as new values appear in data */
  const clientOptions      = useMemo(() => [...new Set(documente.map(d => d.client_name))].sort(), [documente]);
  const proceduraOptions   = useMemo(() => [...new Set(documente.map(d => d.procedura_label).filter(Boolean))].sort(), [documente]);
  const tipServiciuOptions = useMemo(() => [...new Set(documente.map(d => d.tip_serviciu).filter(Boolean))].sort(), [documente]);

  const filtered = useMemo(() => documente.filter(d => {
    if (fClient      && d.client_name     !== fClient)      return false;
    if (fProcedura   && d.procedura_label !== fProcedura)   return false;
    if (fTipServiciu && d.tip_serviciu    !== fTipServiciu) return false;
    return true;
  }), [documente, fClient, fProcedura, fTipServiciu]);

  const hasFilters = fTip || fStatus || fCompanie || fDataFrom || fDataTo || fClient || fProcedura || fTipServiciu;

  /* Facturi summary — recomputed from the currently-filtered document list.
     total / total_fara_tva are already stored in RON. "Incasate" = fully
     collected invoices (status_plata === 'platita'). */
  const facturiSummary = useMemo(() => {
    const acc = {
      taxe:     { n: 0, total: 0, net: 0 },
      onorariu: { n: 0, total: 0, net: 0 },
      mixt:     { n: 0, total: 0, net: 0 },
    };
    const incasate = { n: 0, total: 0, net: 0 };
    const total    = { n: 0, total: 0, net: 0 };
    filtered.forEach(d => {
      const val = parseFloat(d.total) || 0;
      const net = parseFloat(d.total_fara_tva) || 0;
      const b = acc[d.tip_serviciu];
      if (b) { b.n += 1; b.total += val; b.net += net; }
      if (d.status_plata === 'platita') { incasate.n += 1; incasate.total += val; incasate.net += net; }
      total.n += 1; total.total += val; total.net += net;
    });
    return { acc, incasate, total };
  }, [filtered]);

  const resetFilters = () => {
    setFTip(''); setFStatus(''); setFCompanie('');
    setFDataFrom(''); setFDataTo('');
    setFClient(''); setFProcedura(''); setFTipServiciu('');
  };

  /* ── Obiecte tab data ── */
  const loadObiecte = useCallback(() => {
    setOLoading(true);
    const params = { tip: 'fiscala' };
    if (oCompanie) params.companie  = oCompanie;
    if (oDataFrom) params.data_from = oDataFrom;
    if (oDataTo)   params.data_to   = oDataTo;
    smartbillSvc.getAllDocuments(params)
      .then(r => setODocs(r.data.documente || []))
      .catch(() => showToast('Eroare la incarcarea obiectelor'))
      .finally(() => setOLoading(false));
  }, [oCompanie, oDataFrom, oDataTo]); // eslint-disable-line

  useEffect(() => { if (activeTab === 'obiecte') loadObiecte(); }, [activeTab, loadObiecte]);

  const obiecteRows = useMemo(() => {
    const rows = [];
    oDocs.forEach(doc => {
      if (doc.tip !== 'fiscala') return;
      (doc.produse || []).forEach((p, i) => {
        const { netRon, tvaRon, grossRon } = lineAmountsRon(p, doc.curs_valutar || 1);
        rows.push({
          key: `${doc.id}-${i}`,
          doc,
          client_name:     doc.client_name,
          name:            p.name || '—',
          tip:             resolveLineTip(p, doc),
          qty:             p.quantity,
          price:           p.price,
          moneda:          doc.moneda,
          netRon, tvaRon, grossRon,
          status_plata:    doc.status_plata,
          data_emitere:    doc.data_emitere,
          serie:           doc.serie,
          numar:           doc.numar,
          companie:        doc.companie,
          procedura_label: doc.procedura_label,
        });
      });
    });
    return rows;
  }, [oDocs]);

  const obiecteClientOptions = useMemo(
    () => [...new Set(obiecteRows.map(r => r.client_name))].sort(),
    [obiecteRows],
  );

  // rows matching every filter EXCEPT the tip-serviciu one (used for the
  // "hidden untyped" hint) — untyped lines are legacy data with no way to know
  // whether they were taxe or onorariu, so they never enter the view/sums.
  const obiecteBase = useMemo(() => obiecteRows.filter(r => {
    if (oClient && r.client_name !== oClient) return false;
    if (oStatus && !(OBJ_STATUS_GROUPS[oStatus] || []).includes(r.status_plata)) return false;
    return true;
  }), [obiecteRows, oClient, oStatus]);

  const obiecteFiltered = useMemo(() => obiecteBase.filter(r => {
    if (r.tip === 'nespecificat') return false;
    if (oTipServiciu && r.tip !== oTipServiciu) return false;
    return true;
  }), [obiecteBase, oTipServiciu]);

  const nespecificatCount = useMemo(
    () => obiecteBase.filter(r => r.tip === 'nespecificat').length,
    [obiecteBase],
  );

  const obiecteSummary = useMemo(() => {
    const acc = {
      taxe:     { n: 0, net: 0, gross: 0 },
      onorariu: { n: 0, net: 0, gross: 0 },
    };
    obiecteFiltered.forEach(r => {
      const b = acc[r.tip];
      if (!b) return;
      b.n += 1; b.net += r.netRon; b.gross += r.grossRon;
    });
    return {
      acc,
      total: {
        n:     obiecteFiltered.length,
        net:   obiecteFiltered.reduce((s, r) => s + r.netRon, 0),
        gross: obiecteFiltered.reduce((s, r) => s + r.grossRon, 0),
      },
    };
  }, [obiecteFiltered]);

  const oHasFilters = oCompanie || oDataFrom || oDataTo || oClient || oTipServiciu || oStatus;
  const oResetFilters = () => {
    setOCompanie(''); setODataFrom(''); setODataTo('');
    setOClient(''); setOTipServiciu(''); setOStatus('');
  };

  const handleDownload = async (doc) => {
    if (!doc.numar || downloading === doc.id) return;
    setDownloading(doc.id);
    try {
      const res = await smartbillSvc.downloadPdf(doc.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.serie}-${doc.numar}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Eroare la descarcare PDF');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <main className="cp-main">
      <div className="topbar">
        <div className="topbar-left">
          <h1 className="page-title">Facturare</h1>
          <span className="count-badge">{activeTab === 'obiecte' ? obiecteFiltered.length : documente.length}</span>
        </div>
        <div className="topbar-right">
          {reserveLow && (
            <span className="reserve-low-msg">Fonduri rezerva sub 500 RON!</span>
          )}
          <button type="button" className="btn btn-secondary btn-sm" onClick={openReserve}>
            Actualizeaza suma borderou
          </button>
        </div>
      </div>

      <div className="content">
        <div className="type-tabs" style={{ marginBottom: 18 }}>
          <div className={`type-tab${activeTab === 'facturi' ? ' tt-active' : ''}`} onClick={() => setActiveTab('facturi')}>Facturi</div>
          <div className={`type-tab${activeTab === 'obiecte' ? ' tt-active' : ''}`} onClick={() => setActiveTab('obiecte')}>Obiecte</div>
        </div>

        {activeTab === 'facturi' && (<>
        <div className="filter-bar">
          <FilterPill value={fTip} onChange={e => setFTip(e.target.value)}>
            <option value="">Tip</option>
            <option value="proforma">Proforma</option>
            <option value="fiscala">Fiscala</option>
          </FilterPill>

          <FilterPill value={fStatus} onChange={e => setFStatus(e.target.value)}>
            <option value="">Status</option>
            <option value="neplatita">Neplatita</option>
            <option value="platita">Platita / Incasata</option>
            <option value="incasata_partial">Incasat partial</option>
            <option value="facturata">Facturata</option>
            <option value="facturata_partial">Facturata partial</option>
            <option value="depasita">Depasita</option>
            <option value="anulata">Anulata</option>
            <option value="stornata">Stornata</option>
          </FilterPill>

          <FilterPill value={fCompanie} onChange={e => setFCompanie(e.target.value)}>
            <option value="">Companie</option>
            <option value="WAP">WAP</option>
            <option value="Cabinet">Cabinet</option>
            <option value="Inventa">Inventa</option>
          </FilterPill>

          <FilterPill value={fClient} onChange={e => setFClient(e.target.value)}>
            <option value="">Client</option>
            {clientOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </FilterPill>

          <FilterPill value={fProcedura} onChange={e => setFProcedura(e.target.value)}>
            <option value="">Procedura</option>
            {proceduraOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </FilterPill>

          <FilterPill value={fTipServiciu} onChange={e => setFTipServiciu(e.target.value)}>
            <option value="">Tip serviciu</option>
            {tipServiciuOptions.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </FilterPill>

          <FilterDateRange
            from={fDataFrom} to={fDataTo}
            onFromChange={e => setFDataFrom(e.target.value)}
            onToChange={e => setFDataTo(e.target.value)}
          />

          {hasFilters && <FilterClearBtn onClick={resetFilters} />}

          <FilterCountHint count={filtered.length} singular="document" plural="documente" />
        </div>

        <div className="fct-obj-summary">
          {[
            { k: 'taxe',     label: 'Taxe',     d: facturiSummary.acc.taxe },
            { k: 'onorariu', label: 'Onorarii', d: facturiSummary.acc.onorariu },
            { k: 'mixt',     label: 'Mixt',     d: facturiSummary.acc.mixt },
            { k: 'incasate', label: 'Incasate', d: facturiSummary.incasate },
          ].map(({ k, label, d }) => (
            <div key={k} className={`fct-obj-card fct-obj-card--${k}`}>
              <span className="fct-obj-card-label">{label}</span>
              <span className="fct-obj-card-val">{fmtMoney(d.total, 'RON')}</span>
              <span className="fct-obj-card-sub">
                {d.n} {d.n === 1 ? 'factura' : 'facturi'} · net {fmtMoney(d.net, 'RON')}
              </span>
            </div>
          ))}
          <div className="fct-obj-card fct-obj-card--total">
            <span className="fct-obj-card-label">Total</span>
            <span className="fct-obj-card-val">{fmtMoney(facturiSummary.total.total, 'RON')}</span>
            <span className="fct-obj-card-sub">
              {facturiSummary.total.n} {facturiSummary.total.n === 1 ? 'factura' : 'facturi'} · net {fmtMoney(facturiSummary.total.net, 'RON')}
            </span>
          </div>
        </div>

        <div className="clients-card">
          <table className="clients-table op-table fct-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Tip</th>
                <th>Serie / Nr.</th>
                <th>Companie</th>
                <th>Tip serviciu</th>
                <th>Data emitere</th>
                <th>Data scadenta</th>
                <th>Status</th>
                <th>Total</th>
                <th>Procedura</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="op-center-cell">Se incarca...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={11} className="op-center-cell">Niciun document gasit.</td></tr>
              )}
              {filtered.map(doc => {
                return (
                  <tr key={doc.id} onClick={() => setEditDoc(doc)} style={{ cursor: 'pointer' }}>
                    <td className="fct-client-cell">{doc.client_name}</td>
                    <td>
                      <span className={`fin-doc-badge fin-doc-badge--tip${doc.tip === 'fiscala' ? '' : ' fin-doc-badge--proforma'}`}>
                        {doc.tip === 'fiscala' ? 'Fiscala' : 'Proforma'}
                      </span>
                    </td>
                    <td className="fct-nr-cell">
                      {doc.serie}{doc.numar ? `-${doc.numar}` : ''}
                      {doc.este_ciorna && <span className="fct-ciorna-badge">ciorna</span>}
                    </td>
                    <td>
                      <span className={`fin-doc-badge fin-doc-badge--svc fin-doc-badge--${doc.tip_serviciu}`}>
                        {doc.companie}
                      </span>
                    </td>
                    <td className="fct-ts-cell">
                      {doc.tip_serviciu ? doc.tip_serviciu.charAt(0).toUpperCase() + doc.tip_serviciu.slice(1) : '—'}
                    </td>
                    <td>{fmtDate(doc.data_emitere)}</td>
                    <td>{fmtDate(doc.data_scadenta)}</td>
                    <td className="fct-status-cell" onClick={e => e.stopPropagation()}>
                      <FilterPill
                        value={doc.status_plata}
                        onChange={e => handleStatusChange(doc.id, e.target.value)}
                      >
                        {(doc.tip === 'fiscala'
                          ? [
                              { value: '', label: '— Status —' },
                              { value: 'neplatita', label: 'Neplatita' },
                              { value: 'incasata_partial', label: 'Incasat partial' },
                              { value: 'platita', label: 'Incasata' },
                              { value: 'anulata', label: 'Anulata' },
                              { value: 'stornata', label: 'Stornata' },
                            ]
                          : [
                              { value: '', label: '— Status —' },
                              { value: 'neplatita', label: 'Neplatita' },
                              { value: 'facturata', label: 'Facturata' },
                              { value: 'facturata_partial', label: 'Facturata partial' },
                              { value: 'platita', label: 'Platita' },
                              { value: 'depasita', label: 'Depasita' },
                              { value: 'anulata', label: 'Anulata' },
                              { value: 'stornata', label: 'Stornata' },
                            ]
                        ).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </FilterPill>
                    </td>
                    <td className="fct-total-cell">{fmtMoney(doc.total, doc.moneda)}</td>
                    <td className="fct-proc-cell">{doc.procedura_label || '—'}</td>
                    <td className="fct-action-cell" onClick={e => e.stopPropagation()}>
                      {doc.numar && (
                        <button
                          type="button"
                          className="icon-circle-btn"
                          onClick={() => handleDownload(doc)}
                          disabled={downloading === doc.id}
                          title="Descarca PDF"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>)}

        {activeTab === 'obiecte' && (<>
        <div className="filter-bar">
          <FilterPill value={oCompanie} onChange={e => setOCompanie(e.target.value)}>
            <option value="">Companie</option>
            <option value="WAP">WAP</option>
            <option value="Cabinet">Cabinet</option>
            <option value="Inventa">Inventa</option>
          </FilterPill>

          <FilterPill searchable value={oClient} onChange={e => setOClient(e.target.value)}>
            <option value="">Client</option>
            {obiecteClientOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </FilterPill>

          <FilterPill value={oTipServiciu} onChange={e => setOTipServiciu(e.target.value)}>
            <option value="">Tip serviciu</option>
            <option value="taxe">Taxe</option>
            <option value="onorariu">Onorariu</option>
          </FilterPill>

          <FilterPill value={oStatus} onChange={e => setOStatus(e.target.value)}>
            {OBJ_STATUS_LABELS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </FilterPill>

          <FilterDateRange
            from={oDataFrom} to={oDataTo}
            onFromChange={e => setODataFrom(e.target.value)}
            onToChange={e => setODataTo(e.target.value)}
          />

          {oHasFilters && <FilterClearBtn onClick={oResetFilters} />}

          <FilterCountHint count={obiecteFiltered.length} singular="obiect" plural="obiecte" />
          {nespecificatCount > 0 && (
            <span className="fct-obj-hidden-hint">
              {nespecificatCount} {nespecificatCount === 1 ? 'obiect fără tip' : 'obiecte fără tip'} (neincluse)
            </span>
          )}
        </div>

        <div className="fct-obj-summary">
          {['taxe', 'onorariu'].map(k => (
            <div key={k} className={`fct-obj-card fct-obj-card--${k}`}>
              <span className="fct-obj-card-label">{OBJ_SVC_LABEL[k]}</span>
              <span className="fct-obj-card-val">{fmtMoney(obiecteSummary.acc[k].gross, 'RON')}</span>
              <span className="fct-obj-card-sub">
                {obiecteSummary.acc[k].n} obiecte · net {fmtMoney(obiecteSummary.acc[k].net, 'RON')}
              </span>
            </div>
          ))}
          <div className="fct-obj-card fct-obj-card--total">
            <span className="fct-obj-card-label">Total</span>
            <span className="fct-obj-card-val">{fmtMoney(obiecteSummary.total.gross, 'RON')}</span>
            <span className="fct-obj-card-sub">
              {obiecteSummary.total.n} obiecte · net {fmtMoney(obiecteSummary.total.net, 'RON')}
            </span>
          </div>
        </div>

        <div className="clients-card">
          <table className="clients-table op-table fct-obj-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Obiect</th>
                <th>Tip serviciu</th>
                <th>Serie / Nr.</th>
                <th>Companie</th>
                <th>Data emitere</th>
                <th>Status</th>
                <th>Net (RON)</th>
                <th>Total (RON)</th>
                <th>Procedura</th>
              </tr>
            </thead>
            <tbody>
              {oLoading && (
                <tr><td colSpan={10} className="op-center-cell">Se incarca...</td></tr>
              )}
              {!oLoading && obiecteFiltered.length === 0 && (
                <tr><td colSpan={10} className="op-center-cell">Niciun obiect gasit.</td></tr>
              )}
              {!oLoading && obiecteFiltered.map(r => {
                const sc = STATUS_COLORS[r.status_plata] || {};
                return (
                  <tr key={r.key} onClick={() => setEditDoc(r.doc)} style={{ cursor: 'pointer' }}>
                    <td className="fct-client-cell">{r.client_name}</td>
                    <td>{r.name}</td>
                    <td>
                      <span className={`fin-doc-badge fin-doc-badge--svc fin-doc-badge--${r.tip}`}>
                        {OBJ_SVC_LABEL[r.tip]}
                      </span>
                    </td>
                    <td className="fct-nr-cell">{r.serie}{r.numar ? `-${r.numar}` : ''}</td>
                    <td>{r.companie}</td>
                    <td>{fmtDate(r.data_emitere)}</td>
                    <td>
                      <span className="fct-status-badge" style={{ background: sc.bg, color: sc.color }}>
                        {getStatusLabel(r.status_plata, 'fiscala')}
                      </span>
                    </td>
                    <td className="fct-total-cell">{r.netRon.toFixed(2)}</td>
                    <td className="fct-total-cell">{r.grossRon.toFixed(2)}</td>
                    <td className="fct-proc-cell">{r.procedura_label || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>)}
      </div>

      {editDoc && (
        <InvoiceEditModal
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={updated => {
            patchDocEverywhere(updated);
            setEditDoc(null);
          }}
          onConverted={fiscala => {
            setDocumente(ds => [fiscala, ...ds]);
            setODocs(ds => [fiscala, ...ds]);
            setEditDoc(null);
          }}
          onConvert={doc => { setConvertDocFct(doc); setEditDoc(null); }}
          onReEdit={doc => { setReEditDoc(doc); setEditDoc(null); }}
          showToast={showToast}
        />
      )}

      {reEditDoc && (
        <FacturaModal
          client={{ id: reEditDoc.client_id }}
          editDoc={reEditDoc}
          onClose={() => setReEditDoc(null)}
          onSaved={doc => { setDocumente(ds => [doc, ...ds]); setODocs(ds => [doc, ...ds]); setReEditDoc(null); }}
          showToast={showToast}
        />
      )}

      {convertDocFct && (
        <FacturaModal
          client={{ id: convertDocFct.client_id }}
          convertDoc={convertDocFct}
          onClose={() => setConvertDocFct(null)}
          onSaved={doc => { setDocumente(ds => [doc, ...ds]); setODocs(ds => [doc, ...ds]); setConvertDocFct(null); }}
          showToast={showToast}
        />
      )}

      {showReserve && (
        <div className="modal-overlay" onClick={() => !reserveBusy && setShowReserve(false)}>
          <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-sticky-header" style={{ padding: '20px 22px 12px' }}>
              <strong style={{ fontSize: 15 }}>Sold rezervă borderou</strong>
            </div>
            <div style={{ padding: '4px 22px 20px' }}>
              <p className="f-hint" style={{ marginTop: 0 }}>
                Valoare curentă:{' '}
                <strong>{reserveInfo ? `${reserveInfo.sold} RON` : 'se încarcă…'}</strong>
                {reserveInfo?.updated_by && (
                  <> · ultima modificare de {reserveInfo.updated_by}</>
                )}
              </p>
              <div className="field">
                <label>Sold nou (RON)</label>
                <input
                  type="number" step="0.01"
                  value={reserveInput}
                  onChange={e => setReserveInput(e.target.value)}
                  placeholder="ex: 5000"
                  autoFocus
                />
              </div>
              <p className="f-hint" style={{ marginBottom: 0 }}>
                Fiecare borderou OSIM generat scade din această sumă.
              </p>
            </div>
            <div className="fm-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowReserve(false)} disabled={reserveBusy}>
                Renunță
              </button>
              <button type="button" className="btn btn-primary" onClick={saveReserve} disabled={reserveBusy || !reserveInfo}>
                {reserveBusy ? 'Se salvează…' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="toast-wrap">
        {toasts.map(t => <div key={t.id} className="toast">{t.msg}</div>)}
      </div>
    </main>
  );
}
